import React from 'react';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import { format, isValid, parse } from 'date-fns';

/**
 * @typedef {import('../../types/ScheduleValue').ScheduleValue } ScheduleValue
 */

/**
 * Schedule form component for editing schedule details.
 * @param {Object} props - The component props.
 * @param {ScheduleValue} props.currentValue - The current schedule value.
 * @param {string} props.currentValue.date - The scheduled date in YYYYMMDD format.
 * @param {string} props.currentValue.time - The scheduled time in HHMM format.
 * @param {number} props.currentValue.days - The bitmask for days of the week.
 * @param {number} props.currentValue.recur - The recurrence type (0 = one off, 1 = daily, 2 = selected days of week).
 * @param {function(ScheduleValue): void} props.onChange - Callback function to handle changes in the schedule value.
 * @returns {JSX.Element} The rendered component.
 */
function ScheduleForm({ currentValue, onChange }) {
  /**
   * Handles changes to the date selection in the DatePicker component, updating the local state and invoking the onChange callback with the new schedule value.
   * @param {Date} date - The new date selected in the DatePicker.
   */
  const datePickerDateChange = date => {
    onChange({ ...currentValue, date: date ? format(date, 'yyyyMMdd') : '' });
  };

  /**
   * Parses the stored date string into a DatePicker-compatible Date.
   * @param {string} dateString - The date string in YYYYMMDD format from the schedule value.
   * @returns {Date|string} A Date object for the DatePicker or an empty string if the input is invalid.
   */
  const formatDateForDatePicker = dateString => {
    if (!dateString || !/^\d{8}$/.test(dateString)) {
      return '';
    }

    const parsedDate = parse(dateString, 'yyyyMMdd', new Date());

    return isValid(parsedDate) ? parsedDate : '';
  };

  /**
   * Handles changes to the time input, updating the local state and invoking the onChange callback with the new schedule value.
   * @param {Event} event - The change event from the time input.
   */
  const timeChange = event => {
    onChange({ ...currentValue, time: event.target.value.replace(':', '') });
  };

  /**
   * Formats the time string from HHMM format to HH:MM for display in the time input.
   * @param {string} timeString - The time string in HHMM format from the schedule value.
   * @returns {string} The formatted time string in HH:MM format for the input, or an empty string if the input is invalid.
   */
  const formatTimeForInput = timeString => {
    if (!timeString) {
      return '';
    }

    const hours = timeString.slice(0, 2).padStart(2, '0');
    const minutes = timeString.slice(2).padStart(2, '0');

    return `${hours}:${minutes}`;
  };

  /**
   * Handles changes to the recurrence selection, updating the local state and invoking the onChange callback with the new schedule value.
   * @param {Event} event - The change event from the recurrence select input.
   */
  const recurChange = event => {
    const recur = Number(event.target.value);
    const days = recur === 2 ? currentValue.days : 0; // reset days if not selecting specific days

    onChange({ ...currentValue, recur, days });
  };

  /**
   * Handles changes to the day selection, updating the local state and invoking the onChange callback with the new schedule value.
   * @param {Event} event - The change event from the day checkbox input.
   */
  const dayChange = event => {
    let newDays = currentValue.days;

    if (event.target.checked) {
      newDays += Number(event.target.value);
    } else {
      newDays -= Number(event.target.value);
    }

    onChange({ ...currentValue, days: newDays });
  };

  /**
   * Helper function to determine if a specific day is selected based on the bitmask in the current schedule value.
   * @param {number} dayValue - The bitmask value for the specific day (e.g., 1 for Sunday, 2 for Monday, etc.).
   * @returns {boolean} True if the day is selected, false otherwise.
   */
  const isDayChecked = dayValue => {
    // eslint-disable-next-line no-bitwise
    return (currentValue.days & dayValue) === dayValue;
  };

  return (
    <div className="scheduling-form" data-test="schedule-form">
      <div className="schedule-editor">
        <div data-test="schedule-date-field">
          <span>Start Date</span>
          <DatePicker
            dateFormat="dd/MM/yyyy"
            className="form-control"
            placeholderText="dd/mm/yyyy"
            selected={formatDateForDatePicker(currentValue.date)}
            todayButton="Today"
            onChange={datePickerDateChange}
          />
        </div>
        <div>
          <span>Time & Repeat</span>
          <span className="time-group">
            <input
              data-test="schedule-time-input"
              type="time"
              className="form-control"
              value={formatTimeForInput(currentValue.time)}
              onChange={timeChange}
            />
            <select
              data-test="schedule-recur-select"
              className="form-control"
              value={currentValue.recur}
              onChange={recurChange}
            >
              <option value="0">One off</option>
              <option value="1">Daily</option>
              <option value="2">Specific days</option>
            </select>
          </span>
        </div>
        {currentValue.recur === 2 && (
          <div data-test="schedule-days-section">
            <span>Days</span>
            <div className="day-checkboxes">
              <label>
                <input
                  type="checkbox"
                  value="2"
                  data-test="schedule-day-mon"
                  checked={isDayChecked(2)}
                  className="hidden"
                  onChange={dayChange}
                />
                <span>Mo</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  value="4"
                  data-test="schedule-day-tue"
                  checked={isDayChecked(4)}
                  className="hidden"
                  onChange={dayChange}
                />
                <span>Tu</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  value="8"
                  data-test="schedule-day-wed"
                  checked={isDayChecked(8)}
                  className="hidden"
                  onChange={dayChange}
                />
                <span>We</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  value="16"
                  data-test="schedule-day-thu"
                  checked={isDayChecked(16)}
                  className="hidden"
                  onChange={dayChange}
                />
                <span>Th</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  value="32"
                  data-test="schedule-day-fri"
                  checked={isDayChecked(32)}
                  className="hidden"
                  onChange={dayChange}
                />
                <span>Fr</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  value="64"
                  data-test="schedule-day-sat"
                  checked={isDayChecked(64)}
                  className="hidden"
                  onChange={dayChange}
                />
                <span>Sa</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  value="1"
                  data-test="schedule-day-sun"
                  checked={isDayChecked(1)}
                  className="hidden"
                  onChange={dayChange}
                />
                <span>Su</span>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

ScheduleForm.propTypes = {
  currentValue: PropTypes.shape({
    date: PropTypes.string.isRequired, // YYYYMMDD
    time: PropTypes.string.isRequired, // HHMM
    days: PropTypes.number.isRequired, // bitmask for days of week
    recur: PropTypes.number.isRequired, // 0 = one off, 1 = daily, 2 = selected days of week
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ScheduleForm;
